import React from 'react';
import { Box, ButtonBase, CSSObject, Stack, Theme, Typography, styled } from '@mui/material';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import {
  MEAL_LABEL_KEYS,
  MealReading,
  getMealSwatchStyles,
} from 'features/progress/utils/bloodGlucoseMeals';

export const MealSwatch = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'meal' && prop !== 'size',
})<{ meal: MealReading; size?: number }>(({ meal, size = 8 }) => getMealSwatchStyles(meal, size));

export type LegendFilter = 'all' | MealReading;

const Items = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  columnGap: theme.spacing(0.5),
  rowGap: theme.spacing(1),
  paddingInline: theme.spacing(2),
}));

const itemStyles = (theme: Theme, active: boolean): CSSObject => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 0.625, 1.375),
  '&::after': {
    content: '""',
    position: 'absolute',
    insetInline: 0,
    bottom: 0,
    height: 3,
    borderRadius: 1,
    backgroundColor: active ? theme.palette.primary.main : 'transparent',
  },
});

const notForwarded = (prop: PropertyKey) => prop !== 'active';

const StaticItem = styled('div', { shouldForwardProp: notForwarded })<{ active: boolean }>(
  ({ theme, active }) => itemStyles(theme, active),
);

const TabItem = styled(ButtonBase, { shouldForwardProp: notForwarded })<{ active: boolean }>(
  ({ theme, active }) => itemStyles(theme, active),
);

const Label = styled(Typography, { shouldForwardProp: notForwarded })<{ active: boolean }>(
  ({ theme, active }) => ({
    color: active ? theme.palette.primary.main : theme.palette.grey[800],
    ...theme.typography.navigation,
    fontWeight: theme.typography.fontWeightMedium,
    [theme.breakpoints.up('sm')]: {
      ...theme.typography.callout,
      fontWeight: theme.typography.fontWeightMedium,
    },
  }),
);

export interface BloodGlucoseLegendProps {
  meals: MealReading[];
  /** Pass both to make the legend a filter (adds the "All" tab); omit for a plain legend. */
  filter?: LegendFilter;
  onFilterChange?: (filter: LegendFilter) => void;
}

export const BloodGlucoseLegend = ({ meals, filter, onFilterChange }: BloodGlucoseLegendProps) => {
  const { translation } = useTranslationContext();
  const { isMobile } = useDeviceType();
  const isFilter = filter !== undefined && onFilterChange !== undefined;

  const renderItem = (key: LegendFilter, label: string, meal?: MealReading) => {
    const active = isFilter && filter === key;
    const content = (
      <>
        {meal && <MealSwatch meal={meal} size={isMobile ? 8 : 12} aria-hidden />}
        <Label variant="caption" active={active}>
          {label}
        </Label>
      </>
    );
    return isFilter ? (
      <TabItem key={key} active={active} aria-pressed={active} onClick={() => onFilterChange(key)}>
        {content}
      </TabItem>
    ) : (
      <StaticItem key={key} active={false}>
        {content}
      </StaticItem>
    );
  };

  return (
    <Items>
      {isFilter && meals.length > 0 && renderItem('all', translation('filter_label_all'))}
      {meals.map((meal) => renderItem(meal, translation(MEAL_LABEL_KEYS[meal]), meal))}
    </Items>
  );
};
